/**
 * LE BFR — lot 99, 18/09/2026.
 *
 * Maquette `maquette-analyse-bfr-v5.html`, cadrage « Analyse financière :
 * 1. le BFR » et « 8. les arbitrages ». Quatre sections, deux courbes
 * chacune ; tout point est cliquable et ouvre la liste des pièces qui font la
 * valeur. Filtres communs : activité, client ; disposition 1 ou 2 courbes par
 * ligne ; fenêtre réglable.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { vueEnsemble, type ElementBfr, type Fenetre, type Filtre } from '@stafa/core';
import type { EchecApi } from './api.js';
import { lireBfr } from './apiAnalyse.js';
import {
  clientsDe,
  dateCourte,
  dateFr,
  entreesDe,
  euros,
  eurosCourts,
  FENETRES,
  jours,
  soldesA,
  tableau,
  type BfrLu,
  type Tableau,
} from './bfr.js';
import { CourbeAnalyse } from './CourbeAnalyse.js';
import { BandeauFiabilite } from './BandeauFiabilite.js';
import { PanneauReglages } from './PanneauReglages.js';

export function EcranBfr({
  jeton,
  quandAccesPerdu,
  messageRefus,
  sources,
}: {
  readonly jeton: () => Promise<string | null>;
  readonly quandAccesPerdu: () => void;
  readonly messageRefus: (cause: EchecApi) => string;
  readonly sources: (quandLu: () => void) => ReactNode;
}) {
  const [donnees, setDonnees] = useState<BfrLu | null>(null);
  const [refus, setRefus] = useState<string | null>(null);

  async function charger() {
    const j = await jeton();
    if (j === null) {
      quandAccesPerdu();
      return;
    }
    const r = await lireBfr(j);
    if (r.etat === 'ok') {
      setDonnees(r.valeur);
      setRefus(null);
    } else {
      if (r.cause === 'acces') quandAccesPerdu();
      setRefus(r.message ?? messageRefus(r.cause));
    }
  }

  useEffect(() => {
    void charger();
  }, [jeton]);

  return (
    <VueBfr
      donnees={donnees}
      refus={refus}
      sources={sources(() => void charger())}
      fiabilite={<BandeauFiabilite jeton={jeton} ecran="bfr" />}
      reglages={
        <PanneauReglages
          jeton={jeton}
          quandAccesPerdu={quandAccesPerdu}
          messageRefus={messageRefus}
          quandEnregistre={() => void charger()}
        />
      }
    />
  );
}

type Graphique =
  'brut' | 'net' | 'clients' | 'fournisseurs' | 'dso' | 'dpo' | 'marge' | 'couts';

const TITRES: Readonly<Record<Graphique, string>> = {
  brut: 'BFR brut',
  net: 'BFR net d’affacturage',
  clients: 'Encaissements clients, attendus et réalisés',
  fournisseurs: 'Paiements fournisseurs, attendus et réalisés',
  dso: 'Délai d’encaissement client (DSO)',
  dpo: 'Délai de paiement fournisseur (DPO)',
  marge: 'Marge : chiffre d’affaires − charges',
  couts: 'Coût d’exploitation et coût de structure',
};

export function VueBfr({
  donnees,
  refus,
  sources,
  reglages,
  fiabilite = null,
  detailInitial = null,
}: {
  readonly donnees: BfrLu | null;
  readonly refus: string | null;
  readonly sources: ReactNode;
  readonly reglages: ReactNode;
  /** Le bandeau de fiabilite (lot 108). Nul dans l'apercu de maquette. */
  readonly fiabilite?: ReactNode;
  readonly detailInitial?: { graphique: Graphique; rang: number } | null;
}) {
  const [filtre, setFiltre] = useState<Filtre>({ activite: 'tout', client: 'tout' });
  const [fenetre, setFenetre] = useState<Fenetre>('centre');
  const [deux, setDeux] = useState(false);
  const [detail, setDetail] = useState(detailInitial);
  const [panneau, setPanneau] = useState<'sources' | 'reglages' | null>(null);

  const entrees = useMemo(
    () => (donnees === null ? null : entreesDe(donnees)),
    [donnees],
  );
  const t = useMemo(
    () => (entrees === null ? null : tableau(entrees, filtre, fenetre)),
    [entrees, filtre, fenetre],
  );

  if (donnees === null || t === null) {
    return (
      <main className="corps corps-analyse">
        {refus !== null ? (
          <p className="bandeau bandeau-refus">{refus}</p>
        ) : (
          <p className="attente">Lecture des pièces et des ordres…</p>
        )}
      </main>
    );
  }

  const ens = vueEnsemble(filtre);
  const etiquettes = t.semaines.map((s) => `S${String(s.numero)}`);
  const sous = t.semaines.map((s) => dateCourte(s.lundi));
  const clients = clientsDe(donnees);
  const commun = {
    etiquettes,
    sousEtiquettes: sous,
    courant: t.courant,
    futurDepuis: t.futurDepuis,
  };
  const ouvrir = (graphique: Graphique) => (rang: number) =>
    setDetail({ graphique, rang });
  const sociales = donnees.reglages.charges_sociales === '0.00';

  return (
    <main className="corps corps-analyse">
      <div className="bfr-filtres">
        <div className="ech-champ">
          Activité
          <div className="bfr-segments" role="group" aria-label="Activité">
            {[{ id: 'tout', nom: 'Toutes' }, ...donnees.activites].map((a) => (
              <button
                key={a.id}
                type="button"
                aria-pressed={filtre.activite === a.id}
                onClick={() => setFiltre({ ...filtre, activite: a.id })}
              >
                {a.nom}
              </button>
            ))}
          </div>
        </div>
        <label className="ech-champ">
          Client
          <select
            value={filtre.client}
            onChange={(e) => setFiltre({ ...filtre, client: e.target.value })}
          >
            <option value="tout">Tous les clients</option>
            {clients.map((c) => (
              <option key={c.cle} value={c.cle}>
                {c.nom}
              </option>
            ))}
          </select>
        </label>
        <div className="ech-champ">
          Fenêtre
          <div className="bfr-segments" role="group" aria-label="Fenêtre">
            {FENETRES.map((f) => (
              <button
                key={f.cle}
                type="button"
                aria-pressed={fenetre === f.cle}
                onClick={() => setFenetre(f.cle)}
              >
                {f.nom}
              </button>
            ))}
          </div>
        </div>
        <div className="ech-champ bfr-disposition">
          Disposition
          <div className="bfr-segments" role="group" aria-label="Disposition">
            <button type="button" aria-pressed={!deux} onClick={() => setDeux(false)}>
              1 par ligne
            </button>
            <button type="button" aria-pressed={deux} onClick={() => setDeux(true)}>
              2 par ligne
            </button>
          </div>
        </div>
        <div className="bfr-outils">
          <button
            type="button"
            className="bouton bouton-discret bouton-petit"
            aria-expanded={panneau === 'reglages'}
            onClick={() => setPanneau(panneau === 'reglages' ? null : 'reglages')}
          >
            Réglages
          </button>
          <button
            type="button"
            className="bouton bouton-discret bouton-petit"
            aria-expanded={panneau === 'sources'}
            onClick={() => setPanneau(panneau === 'sources' ? null : 'sources')}
          >
            Données Pennylane
          </button>
        </div>
      </div>
      <p className="ech-sous">
        {dateFr(t.semaines[0]?.lundi ?? donnees.aujourdhui)} →{' '}
        {dateFr(t.semaines[t.semaines.length - 1]?.dimanche ?? donnees.aujourdhui)}
        {' · '}Pièces et règlements : Pennylane · Transports : Stafa (depuis le{' '}
        {dateFr(donnees.debut_stafa)})
        {ens &&
          sociales &&
          ' · Charges sociales non comptées : montant à saisir dans les réglages'}
      </p>
      {panneau === 'sources' && <div className="ech-sources">{sources}</div>}
      {panneau === 'reglages' && <div className="ech-sources">{reglages}</div>}
      {refus !== null && <p className="bandeau bandeau-refus">{refus}</p>}
      {fiabilite}

      <Section titre="1 · Besoin en fonds de roulement" deux={deux}>
        <CourbeAnalyse
          {...commun}
          titre={TITRES.brut}
          sousTitre="clients + non facturé − fournisseurs − affrètements à recevoir − charges sociales − TVA"
          series={[{ nom: 'BFR brut', teinte: 1, valeurs: t.bfr.map((b) => b.brut) }]}
          format={eurosCourts}
          quandClic={ouvrir('brut')}
        />
        <CourbeAnalyse
          {...commun}
          titre={TITRES.net}
          sousTitre="BFR brut − factures cédées au factor"
          series={[{ nom: 'BFR net', teinte: 2, valeurs: t.bfr.map((b) => b.net) }]}
          format={eurosCourts}
          quandClic={ouvrir('net')}
        />
      </Section>
      <Section titre="2 · Règlements attendus et réalisés" deux={deux}>
        <CourbeAnalyse
          {...commun}
          titre={TITRES.clients}
          sousTitre="passé : à l’échéance · à venir : dates de l’échéancier et prévisions"
          series={[
            {
              nom: 'attendus',
              teinte: 1,
              valeurs: t.clients.map((f) => f.attendu),
              pointillee: true,
            },
            { nom: 'réalisés', teinte: 3, valeurs: t.clients.map((f) => f.realise) },
          ]}
          format={eurosCourts}
          quandClic={ouvrir('clients')}
        />
        <CourbeAnalyse
          {...commun}
          titre={TITRES.fournisseurs}
          sousTitre={
            filtre.client === 'tout'
              ? 'passé : à l’échéance · à venir : échéancier et prévisions'
              : 'sans objet pour un client'
          }
          series={[
            {
              nom: 'attendus',
              teinte: 1,
              valeurs: t.fournisseurs.map((f) => f.attendu),
              pointillee: true,
            },
            {
              nom: 'réalisés',
              teinte: 4,
              valeurs: t.fournisseurs.map((f) => f.realise),
            },
          ]}
          format={eurosCourts}
          quandClic={ouvrir('fournisseurs')}
        />
      </Section>
      <Section titre="3 · Délais de règlement" deux={deux}>
        <CourbeAnalyse
          {...commun}
          titre={TITRES.dso}
          sousTitre="comptable : encours ÷ volume TTC 91 j × 91 · réel : médiane des règlements"
          series={[
            { nom: 'comptable', teinte: 1, valeurs: t.dso.map((x) => x.comptable) },
            {
              nom: 'réel',
              teinte: 3,
              valeurs: t.dso.map((x) => x.reel),
              pointillee: true,
            },
          ]}
          format={jours}
          quandClic={ouvrir('dso')}
        />
        <CourbeAnalyse
          {...commun}
          titre={TITRES.dpo}
          sousTitre="mêmes règles, côté fournisseurs"
          series={[
            { nom: 'comptable', teinte: 2, valeurs: t.dpo.map((x) => x.comptable) },
            {
              nom: 'réel',
              teinte: 4,
              valeurs: t.dpo.map((x) => x.reel),
              pointillee: true,
            },
          ]}
          format={jours}
          quandClic={ouvrir('dpo')}
        />
      </Section>
      <Section titre="4 · Marge" deux={deux}>
        <CourbeAnalyse
          {...commun}
          titre={TITRES.marge}
          sousTitre={
            ens
              ? 'CA à la livraison − charges du mois étalées par jour'
              : 'marge sur coûts directs · structure non répartie'
          }
          series={[
            { nom: 'CA HT', teinte: 1, valeurs: t.marge.map((m) => m.ca) },
            {
              nom: 'charges',
              teinte: 4,
              valeurs: t.marge.map((m) =>
                m.exploitation === null ? null : m.exploitation + (m.structure ?? 0),
              ),
              pointillee: true,
            },
            { nom: 'marge', teinte: 3, valeurs: t.marge.map((m) => m.marge) },
          ]}
          format={eurosCourts}
          quandClic={ouvrir('marge')}
        />
        <CourbeAnalyse
          {...commun}
          titre={TITRES.couts}
          sousTitre={
            ens
              ? 'fournisseurs rattachés à une activité, et les autres'
              : 'structure : vue d’ensemble seulement'
          }
          series={[
            {
              nom: 'exploitation',
              teinte: 2,
              valeurs: t.marge.map((m) => m.exploitation),
            },
            ...(ens
              ? [
                  {
                    nom: 'structure',
                    teinte: 4 as const,
                    valeurs: t.marge.map((m) => m.structure),
                  },
                ]
              : []),
          ]}
          format={eurosCourts}
          quandClic={ouvrir('couts')}
        />
      </Section>

      {detail !== null && (
        <FenetreDetail
          graphique={detail.graphique}
          rang={detail.rang}
          t={t}
          donnees={donnees}
          ensemble={ens}
          quandRang={(rang) => setDetail({ ...detail, rang })}
          quandFermer={() => setDetail(null)}
        />
      )}
    </main>
  );
}

function Section({
  titre,
  deux,
  children,
}: {
  readonly titre: string;
  readonly deux: boolean;
  readonly children: ReactNode;
}) {
  return (
    <section className="bfr-section">
      <h2 className="bfr-section-titre">{titre}</h2>
      <div className={deux ? 'bfr-rangee deux' : 'bfr-rangee'}>{children}</div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// La fenêtre de détail
// ---------------------------------------------------------------------------

function Liste({
  titre,
  signe,
  montant,
  elements,
  ouvert = false,
  format = euros,
}: {
  readonly titre: string;
  readonly signe?: '+' | '−' | '';
  readonly montant: number;
  readonly elements: readonly ElementBfr[];
  readonly ouvert?: boolean;
  readonly format?: (c: number) => string;
}) {
  return (
    <details className="bfr-bloc" open={ouvert}>
      <summary>
        <span>
          {signe} {titre} <span className="ech-doux">({elements.length})</span>
        </span>
        <strong>{format(montant)}</strong>
      </summary>
      {elements.length === 0 ? (
        <p className="ech-vide">Aucune pièce.</p>
      ) : (
        <div className="ech-tableau">
          <table>
            <thead>
              <tr>
                <th>Pièce</th>
                <th>Tiers</th>
                <th>Date</th>
                <th>Note</th>
                <th className="nombre">Montant</th>
              </tr>
            </thead>
            <tbody>
              {elements.map((x, i) => (
                <tr key={`${x.cle}-${String(i)}`}>
                  <td>{x.libelle}</td>
                  <td className="ech-tiers" data-etiquette="Tiers">
                    {x.tiers}
                  </td>
                  <td data-etiquette="Date">{dateFr(x.date)}</td>
                  <td className="ech-petit ech-doux ech-origine">{x.note}</td>
                  <td className="nombre ech-montant">{format(x.montant)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </details>
  );
}

function FenetreDetail({
  graphique,
  rang,
  t,
  donnees,
  ensemble,
  quandRang,
  quandFermer,
}: {
  readonly graphique: Graphique;
  readonly rang: number;
  readonly t: Tableau;
  readonly donnees: BfrLu;
  readonly ensemble: boolean;
  readonly quandRang: (r: number) => void;
  readonly quandFermer: () => void;
}) {
  useEffect(() => {
    const touche = (e: KeyboardEvent) => {
      if (e.key === 'Escape') quandFermer();
      if (e.key === 'ArrowLeft' && rang > 0) quandRang(rang - 1);
      if (e.key === 'ArrowRight' && rang < t.semaines.length - 1) quandRang(rang + 1);
    };
    window.addEventListener('keydown', touche);
    return () => window.removeEventListener('keydown', touche);
  }, [rang, quandFermer, quandRang, t.semaines.length]);

  const s = t.semaines[rang];
  if (s === undefined) return null;
  const b = t.bfr[rang];
  const contexte = `Semaine ${String(s.numero)} · du ${dateFr(s.lundi)} au ${dateFr(s.dimanche)} · ${
    s.future
      ? `prévision arrêtée au ${dateFr(s.arrete)}`
      : s.courante
        ? `arrêtée à aujourd’hui`
        : `arrêtée au ${dateFr(s.arrete)}`
  }`;

  let corps: ReactNode = null;
  if ((graphique === 'brut' || graphique === 'net') && b !== undefined) {
    const blocs = b.blocs.filter((x) => graphique === 'net' || x.cle !== 'cedees');
    const soldes = ensemble && !s.future ? soldesA(donnees, s.arrete) : [];
    corps = (
      <>
        <p className="bfr-calcul">
          {blocs
            .map(
              (x) =>
                `${x.signe === 1 ? '+' : '−'} ${euros(x.montant)} (${x.titre.charAt(0).toLowerCase() + x.titre.slice(1)})`,
            )
            .join(' ')}{' '}
          = <strong>{euros(graphique === 'net' ? b.net : b.brut)}</strong>
        </p>
        {blocs.map((x) => (
          <Liste
            key={x.cle}
            titre={x.titre}
            signe={x.signe === 1 ? '+' : '−'}
            montant={x.montant}
            elements={x.elements}
          />
        ))}
        {soldes.length > 0 && (
          <details className="bfr-bloc" open>
            <summary>
              <span>Soldes bancaires au {dateFr(s.arrete)} (pour mémoire)</span>
              <strong>{euros(soldes.reduce((a, x) => a + (x.solde ?? 0), 0))}</strong>
            </summary>
            <ul className="ech-liste">
              {soldes.map((x) => (
                <li key={x.nom}>
                  <span>{x.nom}</span>
                  <span className="nombre">
                    {x.solde === null ? 'solde non lu' : euros(x.solde)}
                  </span>
                </li>
              ))}
            </ul>
          </details>
        )}
      </>
    );
  } else if (graphique === 'clients' || graphique === 'fournisseurs') {
    const f = (graphique === 'clients' ? t.clients : t.fournisseurs)[rang];
    if (f !== undefined) {
      corps = (
        <>
          <p className="bfr-calcul">
            Attendu {euros(f.attendu)}
            {f.realise !== null &&
              ` · réalisé ${euros(f.realise)} · écart ${euros(f.realise - f.attendu)}`}
          </p>
          <Liste
            titre={s.future ? 'Attendus aux dates prévues' : 'Attendus à l’échéance'}
            montant={f.attendu}
            elements={f.attendus}
            ouvert
          />
          {!s.future && (
            <Liste
              titre="Réglés dans la semaine"
              montant={f.realise ?? 0}
              elements={f.realises}
              ouvert
            />
          )}
        </>
      );
    }
  } else if (graphique === 'dso' || graphique === 'dpo') {
    const x = (graphique === 'dso' ? t.dso : t.dpo)[rang];
    if (x !== undefined) {
      corps = (
        <>
          <p className="bfr-calcul">
            Comptable : {euros(x.encours)} ÷ {euros(x.volume)} × 91 ={' '}
            <strong>{x.comptable === null ? '—' : jours(x.comptable)}</strong>
            {' · '}Réel : médiane de {x.regles.length} règlement(s) ={' '}
            <strong>{x.reel === null ? '—' : jours(x.reel)}</strong>
          </p>
          <Liste
            titre="Règlements des 91 derniers jours (délai en note)"
            montant={x.regles.reduce((a, y) => a + y.montant, 0)}
            elements={x.regles}
            ouvert
          />
          {x.exclues.length > 0 && (
            <Liste
              titre="Exclues : dates incohérentes"
              montant={x.exclues.reduce((a, y) => a + y.montant, 0)}
              elements={x.exclues}
              ouvert
            />
          )}
        </>
      );
    }
  } else {
    const m = t.marge[rang];
    if (m !== undefined) {
      corps = m.non_disponible ? (
        <p className="bandeau bandeau-attente">
          Coût non disponible : avant le {dateFr(donnees.debut_stafa)}, Stafa ne
          calculait pas le coût des ordres de ce client.
        </p>
      ) : (
        <>
          <p className="bfr-calcul">
            {euros(m.ca)} de CA − {euros(m.exploitation ?? 0)} d’exploitation
            {m.structure !== null && ` − ${euros(m.structure)} de structure`} ={' '}
            <strong>{euros(m.marge ?? 0)}</strong>
          </p>
          <Liste
            titre="Ventes (HT)"
            signe="+"
            montant={m.ca}
            elements={m.ventes}
            ouvert={graphique === 'marge'}
          />
          <Liste
            titre="Charges (HT, part de la semaine en note)"
            signe="−"
            montant={(m.exploitation ?? 0) + (m.structure ?? 0)}
            elements={m.charges}
            ouvert={graphique === 'couts'}
          />
        </>
      );
    }
  }

  return (
    <div className="ech-voile bfr-voile" role="presentation" onClick={quandFermer}>
      <div
        className="bfr-fenetre"
        role="dialog"
        aria-modal="true"
        aria-label={TITRES[graphique]}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ech-panneau-tete">
          <div>
            <h2>{TITRES[graphique]}</h2>
            <p className="ech-sous">{contexte}</p>
          </div>
          <button
            type="button"
            className="ech-fermer"
            aria-label="Fermer"
            onClick={quandFermer}
          >
            ×
          </button>
        </div>
        <div className="ech-actions">
          <button
            type="button"
            className="bouton bouton-discret bouton-petit"
            disabled={rang === 0}
            onClick={() => quandRang(rang - 1)}
          >
            ◂ Semaine précédente
          </button>
          <button
            type="button"
            className="bouton bouton-discret bouton-petit"
            disabled={rang === t.semaines.length - 1}
            onClick={() => quandRang(rang + 1)}
          >
            Semaine suivante ▸
          </button>
        </div>
        {corps}
      </div>
    </div>
  );
}
